import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Chip, IconButton, Menu, MenuItem,
  ListItemIcon, ListItemText, Paper, Grid, LinearProgress, Collapse,
  Select, FormControl, InputLabel, Tooltip, Alert, useTheme, alpha,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, TableSortLabel,
  TextField,
} from '@mui/material';
import useSortableTable from '../utils/useSortableTable';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Cancel as CancelIcon,
  RestartAlt as RestartIcon,
  Delete as DeleteIcon,
  PauseCircle as PauseIcon,
  VpnKey as KDMIcon,
  Send as DeliverIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  AdjustOutlined as ActiveStepIcon,
} from '@mui/icons-material';
import {
  fetchDCPJobs, fetchDCPJobStats, cancelDCPJob, deleteDCPJob, restartDCPJob,
} from '../store/slices/dcpJobsSlice';
import DeliverDialog from '../components/DeliverDialog';
import PageHeader from '../components/PageHeader';
import StatCards from '../components/StatCards';
import { fmtDateTime, fmtDateTimeSeconds } from '../utils/formatDate';

const STATUS_COLORS = {
  pending: 'default', creating: 'info', building: 'warning',
  qc: 'info', packaging: 'info', completed: 'success', failed: 'error', cancelled: 'default',
};
const STATUS_LABELS = {
  pending: 'Pending', creating: 'Creating', building: 'Encoding',
  qc: 'QC', packaging: 'Packaging', completed: 'Completed', failed: 'Failed', cancelled: 'Cancelled',
};

function fmtDuration(ms) {
  if (!ms || ms < 0) return '—';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function fmtDate(d) {
  if (!d) return '—';
  return fmtDateTimeSeconds(d);
}

function fmtDateShort(d) {
  if (!d) return '—';
  return fmtDateTime(d);
}

function DetailRow({ label, value, mono }) {
  return (
    <Box sx={{ display: 'flex', py: 0.5, gap: 2 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', width: 140, flexShrink: 0 }}>{label}</Typography>
      <Typography variant="caption" sx={{ fontFamily: mono ? 'monospace' : 'inherit', wordBreak: 'break-all' }}>{value || '—'}</Typography>
    </Box>
  );
}

// Compute post-build phase from the data the API already returns, so
// this works even if the backend hasn't been redeployed with the
// `delivery_phase` field. Mirrors the same pipeline order the backend
// version uses; the optional backend fields override when present.
function computePhase(job) {
  const statusVal = (typeof job.status === 'string' ? job.status : (job.status?.value || '')).toLowerCase();
  const packaged = job.delivery_packaged !== undefined ? !!job.delivery_packaged : !!job.package_path;
  const synced = job.delivery_synced !== undefined ? !!job.delivery_synced : !!job.delivery_ready;
  // We can't tell sync-vs-verify from the job alone (hash state lives
  // on the package row), but `delivery_ready` flips true only after
  // synced && hash_status==='done', so once it's true everything is
  // verified. Until then, verify is unfinished.
  const verified = job.delivery_verified !== undefined ? !!job.delivery_verified : !!job.delivery_ready;
  const qcDone = job.delivery_qc_done !== undefined
    ? !!job.delivery_qc_done
    : !!(job.qc_status && job.qc_status !== 'pending' && job.qc_status !== 'running');

  if (job.delivery_phase) return { phase: job.delivery_phase, packaged, synced, verified, qcDone };
  if (!['completed', 'packaging'].includes(statusVal)) return { phase: null, packaged, synced, verified, qcDone };
  // Pipeline order — QC runs BEFORE tar so the qc_report sidecars land
  // inside the deliverable. Check it first so a recovery-path tar that
  // skipped QC reads as "QC outstanding", not "Packaged · ready".
  // Exception: while status='packaging', the live tar is in flight —
  // surface that as the active step regardless of qcDone (means we're
  // already past QC, packaging just hasn't flipped 'packaged' yet).
  if (statusVal === 'packaging') return { phase: 'packaging', packaged, synced, verified, qcDone };
  if (!qcDone) return { phase: 'qc', packaged, synced, verified, qcDone };
  if (!packaged) return { phase: 'packaging', packaged, synced, verified, qcDone };
  if (!synced) return { phase: 'syncing', packaged, synced, verified, qcDone };
  if (!verified) return { phase: 'verifying', packaged, synced, verified, qcDone };
  return { phase: 'ready', packaged, synced, verified, qcDone };
}

// Post-build pipeline checklist — Encoded → QC → Packaged → Synced → Verified.
// Each step is a leading icon (✓ done · ⟳ active · ○ pending) followed by
// a label, with optional substatus when active. Avoids the "100% but
// stuck" reading by showing what's still outstanding even after the
// build itself reaches 100%.
function PostBuildChecklist({ job }) {
  const { phase, packaged, synced, verified, qcDone } = computePhase(job);
  // Format the deliverable will ship as. The actual extension on
  // `package_path` is the source of truth once packaging has produced
  // a file — some legacy rows have `delivery_format='zip'` but a real
  // .tar on disk (built before zip support landed), so trusting the
  // column alone mislabels them. Fall back to the column for in-flight
  // rows where no file exists yet.
  const fmtFromPath = job.package_path
    ? job.package_path.split('.').pop().toLowerCase()
    : null;
  const fmt = (fmtFromPath === 'zip' || fmtFromPath === 'tar')
    ? fmtFromPath
    : (job.delivery_format || 'tar').toLowerCase();
  // Step labels switch from passive to active form when the step is the
  // current in-flight phase — "Packaged" → "Packaging", "Synced" →
  // "Syncing", "Verified" → "Verifying". Reads more naturally than a
  // static past-tense label with a contradicting "…" sub-status.
  const steps = [
    { key: 'encoded',  label: 'Encoded',         done: true,         sub: null },
    { key: 'qc',       label: qcDone ? 'QC' : 'QC running',
                                                 done: qcDone,       sub: !qcDone && job.qc_progress != null
                                                                              ? `${Math.round((job.qc_progress || 0) * 100)}%`
                                                                              : null },
    { key: 'packaged', label: packaged ? `Packaged (.${fmt})` : `Packaging (.${fmt})`,
                                                 done: packaged,     sub: null },
    { key: 'synced',   label: synced ? 'Synced' : 'Syncing',
                                                 done: synced,       sub: phase === 'syncing' ? 'uploading to delivery node' : null },
    { key: 'verified', label: verified ? 'Verified' : 'Verifying',
                                                 done: verified,     sub: phase === 'verifying' ? 'computing SHA256' : null },
  ];
  // The "active" step is the first un-done one. It gets the spinning
  // ⟳ icon + bold text. Steps before it are ✓; after it are ○.
  const activeIdx = steps.findIndex((s) => !s.done);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
      {steps.map((s, i) => {
        const isActive = i === activeIdx;
        const isDone = s.done;
        const Icon = isDone ? CheckCircleIcon : isActive ? ActiveStepIcon : UncheckedIcon;
        const iconColor = isDone ? 'success.main' : isActive ? 'warning.main' : 'text.disabled';
        const textColor = isDone ? 'text.primary' : isActive ? 'warning.main' : 'text.secondary';
        return (
          <Box key={s.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Icon
              sx={{
                fontSize: 14, color: iconColor, flexShrink: 0,
                animation: isActive ? 'pbc-pulse 1.4s ease-in-out infinite' : 'none',
                '@keyframes pbc-pulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%':       { opacity: 0.45 },
                },
              }}
            />
            <Typography variant="caption" sx={{ color: textColor, fontWeight: isActive ? 600 : 400, fontSize: '0.75rem' }}>
              {s.label}
              {s.sub && (
                <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400, ml: 0.5 }}>
                  · {s.sub}
                </Box>
              )}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function DCPJobs() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const { jobs, stats } = useSelector((state) => state.dcpJobs);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [deliverJob, setDeliverJob] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  // ACTIVE rows are anything that hasn't reached "delivery_ready" yet.
  // - pending/creating/building/qc/packaging are pre-delivery states.
  // - completed BUT NOT delivery_ready (tar not yet synced to MinIO) stays
  //   in the queue so the operator sees it finishing the pipeline.
  // - failed/cancelled stay in the queue as a record (not in the library).
  const ACTIVE = new Set(['pending', 'creating', 'building', 'qc', 'packaging', 'failed', 'cancelled']);
  const activeJobs = (jobs || []).filter((j) =>
    ACTIVE.has(j.status) || (j.status === 'completed' && !j.delivery_ready)
  );
  const filteredByStatus = statusFilter ? activeJobs.filter((j) => j.status === statusFilter) : activeJobs;
  const q = searchTerm.trim().toLowerCase();
  const searched = q
    ? filteredByStatus.filter((j) =>
        (j.name || '').toLowerCase().includes(q) ||
        (j.source_filename || '').toLowerCase().includes(q) ||
        (j.unique_id || '').toLowerCase().includes(q)
      )
    : filteredByStatus;
  const { rows: sortedJobs, sortProps } = useSortableTable(
    searched,
    'created_at',
    'desc',
    {
      name: (r) => (r.name || '').toLowerCase(),
      created_at: (r) => (r.created_at ? new Date(r.created_at).getTime() : 0),
      progress: (r) => Number(r.progress || 0),
    },
  );

  const loadData = useCallback(() => {
    dispatch(fetchDCPJobs({ status: statusFilter || undefined }));
    dispatch(fetchDCPJobStats());
  }, [dispatch, statusFilter]);

  useEffect(() => {
    loadData();
    // v10.21.27: gate the 3s poll on tab visibility. Pre-fix this
    // loop fired 20 req/min from every operator with the page open,
    // including backgrounded tabs. Document.hidden tabs return zero
    // information to the operator anyway — keep the cadence for the
    // visible-tab path (DCP encode progress changes minute-by-
    // minute, fine-grained polling is the point) but stop slamming
    // the single uvicorn worker when nobody's looking. Same pattern
    // as Dashboard.jsx + ActiveTransfersPanel.jsx.
    const interval = setInterval(() => {
      if (document.hidden) return;
      loadData();
    }, 3000);
    return () => clearInterval(interval);
  }, [loadData, dispatch]);

  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const handleMenuOpen = (event, job) => { event.stopPropagation(); setAnchorEl(event.currentTarget); setSelectedJob(job); };
  const handleMenuClose = () => { setAnchorEl(null); setSelectedJob(null); };
  const handleCancel = () => { if (selectedJob) dispatch(cancelDCPJob(selectedJob.id)); handleMenuClose(); };
  const handleRestart = () => { if (selectedJob) dispatch(restartDCPJob(selectedJob.id)); handleMenuClose(); };
  const handleDelete = () => { setDeleteConfirm(selectedJob); handleMenuClose(); };

  return (
    <Box>
      <PageHeader
        title="DCP Queue"
        actions={
          <>
            <Tooltip title="Refresh"><IconButton onClick={loadData}><RefreshIcon /></IconButton></Tooltip>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/dcp/create')}>Create DCP</Button>
          </>
        }
      />

      {stats?.screener_blocking && (
        <Alert severity="info" icon={<PauseIcon />} sx={{ mb: 2 }}>
          DCP encoding paused — {stats.screener_pending} Watermark Encode job(s) pending, {stats.screener_processing} processing.
        </Alert>
      )}

      <StatCards stats={stats} />

      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="creating">Creating</MenuItem>
            <MenuItem value="building">Encoding</MenuItem>
            <MenuItem value="qc">QC</MenuItem>
            <MenuItem value="packaging">Packaging</MenuItem>
          </Select>
        </FormControl>
        <TextField
          size="small"
          placeholder="Search project, source, ID…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 280 }}
        />
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }} />
              <TableCell><TableSortLabel {...sortProps('name')}>Project</TableSortLabel></TableCell>
              <TableCell sx={{ width: 120 }}><TableSortLabel {...sortProps('status')}>Status</TableSortLabel></TableCell>
              <TableCell sx={{ width: 160 }}><TableSortLabel {...sortProps('progress')}>Progress</TableSortLabel></TableCell>
              <TableCell sx={{ width: 130 }}><TableSortLabel {...sortProps('created_at')}>Created</TableSortLabel></TableCell>
              <TableCell sx={{ width: 50 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedJobs.map((job) => {
              const isExpanded = expandedId === job.id;
              const encStart = job.started_at ? new Date(job.started_at) : null;
              const encEnd = job.encoding_completed_at ? new Date(job.encoding_completed_at) : null;
              const tarStart = job.packaging_started_at ? new Date(job.packaging_started_at) : null;
              const tarEnd = job.packaging_completed_at ? new Date(job.packaging_completed_at) : null;
              const encDur = encStart && encEnd ? encEnd - encStart : null;
              const tarDur = tarStart && tarEnd ? tarEnd - tarStart : null;
              const isEncoding = ['creating', 'building'].includes(job.status);
              const isPackaging = job.status === 'packaging';
              const _isDone = ['completed', 'failed', 'cancelled'].includes(job.status); // kept for future row-styling

              return (
                <React.Fragment key={job.id}>
                  <TableRow
                    hover
                    onClick={() => setExpandedId(isExpanded ? null : job.id)}
                    sx={{ cursor: 'pointer', '& > *': { borderBottom: isExpanded ? 'none' : undefined } }}
                  >
                    <TableCell>
                      <IconButton size="small" sx={{ p: 0 }}>
                        {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{job.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{job.source_filename}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={STATUS_LABELS[job.status] || job.status} color={STATUS_COLORS[job.status] || 'default'} size="small" />
                    </TableCell>
                    <TableCell>
                      {job.status === 'creating' ? (
                        <Typography variant="caption" color="text.secondary">Creating project on encoder...</Typography>
                      ) : job.status === 'failed' ? (
                        <Typography variant="caption" color="error" sx={{ wordBreak: 'break-word', maxWidth: 300, display: 'block' }}>
                          {(job.error_message || 'Encode failed').substring(0, 150)}
                        </Typography>
                      ) : (() => {
                        // Render the post-build checklist when the build is
                        // done but the deliverable isn't ready yet. We compute
                        // the phase client-side as a fallback so this works
                        // even if the backend hasn't been redeployed with the
                        // `delivery_phase` field.
                        const isPostBuild = job.status === 'completed' || job.status === 'packaging';
                        const phase = job.delivery_phase
                          || (isPostBuild && !job.delivery_ready ? 'pending' : null);
                        return isPostBuild && phase && phase !== 'ready' && !job.delivery_ready;
                      })() ? (
                        <PostBuildChecklist job={job} />
                      ) : (
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress
                              variant={
                                job.status === 'building' && (job.progress || 0) === 0 ? 'indeterminate' : 'determinate'
                              }
                              value={
                                job.status === 'qc'
                                  ? Math.round((job.qc_progress || 0) * 100)
                                  : job.status === 'completed'
                                    ? 100
                                    : job.progress || 0
                              }
                              sx={{ height: 6, borderRadius: 3, flexGrow: 1 }} />
                            <Typography variant="caption" sx={{ minWidth: 30 }}>
                              {job.status === 'qc' ? `${Math.round((job.qc_progress || 0) * 100)}%` :
                               job.status === 'completed' ? '100%' :
                               job.status === 'building' && (job.progress || 0) === 0 ? '' :
                               `${Math.round(job.progress || 0)}%`}
                            </Typography>
                          </Box>
                          {job.status === 'qc' && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              Running automated DCP QC (clairmeta + dcpomatic verify)…
                            </Typography>
                          )}
                          {/* ETA: rough estimate from elapsed time at current
                              progress. Only shown for in-flight builds with
                              non-trivial progress so we don't display "ETA: 5d"
                              for a job that's at 0.1%. */}
                          {(() => {
                            if (job.status !== 'building' && job.status !== 'encoding') return null;
                            const p = Number(job.progress) || 0;
                            if (p < 5 || p >= 99) return null;
                            const startedAt = job.started_at || job.created_at;
                            if (!startedAt) return null;
                            const elapsedSec = (Date.now() - new Date(startedAt).getTime()) / 1000;
                            if (elapsedSec < 60) return null;
                            const totalSec = elapsedSec / (p / 100);
                            const remainingSec = Math.max(0, totalSec - elapsedSec);
                            const fmt = (s) => {
                              if (s < 60) return `${Math.round(s)}s`;
                              if (s < 3600) return `${Math.round(s / 60)}m`;
                              const h = Math.floor(s / 3600);
                              const m = Math.round((s % 3600) / 60);
                              return m === 0 ? `${h}h` : `${h}h ${m}m`;
                            };
                            return (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                ~{fmt(remainingSec)} remaining (rough estimate)
                              </Typography>
                            );
                          })()}
                          {job.status === 'building' && (job.progress || 0) === 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              Analyzing source and preparing frames...
                            </Typography>
                          )}
                          {job.status === 'packaging' && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              Packaging DCP for delivery...
                            </Typography>
                          )}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{fmtDateShort(job.created_at)}</Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, job)}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>

                  {/* Expanded detail panel */}
                  <TableRow>
                    <TableCell sx={{ py: 0 }} colSpan={6}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, px: 2 }}>
                          <Grid container spacing={3}>
                            {/* DCP Settings */}
                            <Grid size={{ xs: 12, md: 4 }}>
                              <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>DCP Settings</Typography>
                              <DetailRow label="Container" value={job.container === 'flat' ? 'Flat (1.85)' : 'Scope (2.39)'} />
                              <DetailRow label="Resolution" value={job.resolution} />
                              <DetailRow label="Frame Rate" value={`${job.frame_rate} fps`} />
                              <DetailRow label="Standard" value={job.standard} />
                              <DetailRow label="Content Type" value={job.content_type} />
                              <DetailRow label="Video Bitrate" value={`${job.video_bitrate} Mbit/s`} />
                              <DetailRow label="Audio Layout" value={job.audio_layout === '5.1' ? '5.1 Surround (51)' : 'Stereo (20)'} />
                              <DetailRow label="Audio Channels" value={`${job.audio_channels}ch MXF`} />
                              <DetailRow label="Audio Language" value={job.audio_language} />
                              <DetailRow label="Priority" value={job.priority} />
                              {job.extra_metadata?.reel_mode && job.extra_metadata.reel_mode !== 'single' && (
                                <>
                                  <DetailRow label="Reel Mode" value={job.extra_metadata.reel_mode.replace('by_', 'By ').replace('_', ' ')} />
                                  {job.extra_metadata.reel_duration_minutes && (
                                    <DetailRow label="Reel Duration" value={`${job.extra_metadata.reel_duration_minutes} min`} />
                                  )}
                                </>
                              )}
                            </Grid>

                            {/* Encryption */}
                            <Grid size={{ xs: 12, md: 4 }}>
                              <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>Encryption</Typography>
                              <DetailRow label="Encrypted" value={job.encrypt ? 'Yes' : 'No'} />
                              {job.encrypt && (
                                <>
                                  <DetailRow label="Picture" value={job.encrypt_picture ? 'Encrypted' : 'Open'} />
                                  <DetailRow label="Sound" value={job.encrypt_sound ? 'Encrypted' : 'Open'} />
                                  <DetailRow label="Text" value={job.encrypt_text ? 'Encrypted' : 'Open'} />
                                </>
                              )}
                              {job.ffec_frame != null && <DetailRow label="FFEC Frame" value={job.ffec_frame} />}
                              {job.ffmc_frame != null && <DetailRow label="FFMC Frame" value={job.ffmc_frame} />}

                              <Divider sx={{ my: 1.5 }} />
                              <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>Node</Typography>
                              <DetailRow label="Job ID" value={job.unique_id} mono />
                              <DetailRow label="DCP Node" value={job.dcp_node_id ? `Node #${job.dcp_node_id}` : 'Unassigned'} />
                              <DetailRow label="Create Job ID" value={job.node_create_job_id} mono />
                              <DetailRow label="Build Job ID" value={job.node_build_job_id} mono />
                            </Grid>

                            {/* Timing & Output */}
                            <Grid size={{ xs: 12, md: 4 }}>
                              <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>Timing</Typography>
                              <DetailRow label="Created" value={fmtDate(job.created_at)} />
                              <DetailRow label="Encode Started" value={fmtDate(job.started_at)} />
                              <DetailRow label="Encode Completed" value={fmtDate(job.encoding_completed_at)} />
                              <DetailRow label="Encode Duration" value={encDur ? fmtDuration(encDur) : isEncoding ? 'In progress' : '—'} />
                              {(() => {
                                const srcDur = job.extra_metadata?.source_duration_seconds;
                                if (srcDur && encDur) {
                                  const ratio = (srcDur * 1000) / encDur;
                                  return <DetailRow label="Encode Speed" value={`${ratio.toFixed(2)}x realtime (${fmtDuration(srcDur * 1000)} source in ${fmtDuration(encDur)})`} />;
                                }
                                if (srcDur) return <DetailRow label="Source Duration" value={fmtDuration(srcDur * 1000)} />;
                                return null;
                              })()}
                              <DetailRow label="Tar Started" value={fmtDate(job.packaging_started_at)} />
                              <DetailRow label="Tar Completed" value={fmtDate(job.packaging_completed_at)} />
                              <DetailRow label="Tar Duration" value={tarDur ? fmtDuration(tarDur) : isPackaging ? 'In progress' : '—'} />

                              <Divider sx={{ my: 1.5 }} />
                              <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>Source</Typography>
                              <DetailRow label="Filename" value={job.source_filename} mono />
                              {job.extra_metadata?.source_resolution && <DetailRow label="Resolution" value={job.extra_metadata.source_resolution} />}
                              {job.extra_metadata?.source_fps && <DetailRow label="Frame Rate" value={`${job.extra_metadata.source_fps} fps`} />}
                              {job.extra_metadata?.source_codec && <DetailRow label="Codec" value={job.extra_metadata.source_codec} />}
                              {job.extra_metadata?.source_audio_channels && <DetailRow label="Audio Channels" value={`${job.extra_metadata.source_audio_channels}ch`} />}
                              {job.extra_metadata?.source_duration_seconds && <DetailRow label="Duration" value={fmtDuration(job.extra_metadata.source_duration_seconds * 1000)} />}

                              <Divider sx={{ my: 1.5 }} />
                              <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>Output</Typography>
                              <DetailRow label="Content Path" value={job.content_path} mono />
                              <DetailRow label="Output Path" value={job.output_path} mono />
                              <DetailRow label="Package Path" value={job.package_path} mono />
                              {job.error_message && (
                                <Box sx={{ mt: 1, p: 1.5, bgcolor: alpha(theme.palette.error.main, 0.08), borderRadius: 1, border: `1px solid ${alpha(theme.palette.error.main, 0.2)}` }}>
                                  <Typography variant="caption" color="error" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{job.error_message}</Typography>
                                </Box>
                              )}
                            </Grid>
                          </Grid>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
            {jobs.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No DCP jobs{statusFilter ? ` with status "${statusFilter}"` : ''}.</Typography>
                  <Button size="small" onClick={() => navigate('/dcp/create')} sx={{ mt: 1 }}>Create DCP</Button>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Actions menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        {selectedJob?.status === 'completed' && selectedJob?.encrypt && (
          <MenuItem onClick={() => { handleMenuClose(); navigate(`/dcp/${selectedJob.id}/distribute`); }}>
            <ListItemIcon><KDMIcon fontSize="small" color="primary" /></ListItemIcon>
            <ListItemText>KDM Distribute</ListItemText>
          </MenuItem>
        )}
        {selectedJob?.status === 'completed' && selectedJob?.package_path && (
          <MenuItem onClick={() => { setDeliverJob(selectedJob); handleMenuClose(); }}>
            <ListItemIcon><DeliverIcon fontSize="small" color="primary" /></ListItemIcon>
            <ListItemText>Deliver</ListItemText>
          </MenuItem>
        )}
        {selectedJob?.status === 'completed' && !selectedJob?.package_path && (
          <MenuItem disabled>
            <ListItemIcon><DeliverIcon fontSize="small" color="disabled" /></ListItemIcon>
            <ListItemText>Packaging for delivery...</ListItemText>
          </MenuItem>
        )}
        {selectedJob?.status === 'failed' && (
          <MenuItem onClick={handleRestart}>
            <ListItemIcon><RestartIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Restart</ListItemText>
          </MenuItem>
        )}
        {['pending', 'creating', 'building'].includes(selectedJob?.status) && (
          <MenuItem onClick={handleCancel}>
            <ListItemIcon><CancelIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Cancel</ListItemText>
          </MenuItem>
        )}
        {['completed', 'failed', 'cancelled'].includes(selectedJob?.status) && (
          <MenuItem onClick={handleDelete}>
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {deliverJob && (
        <DeliverDialog
          open={Boolean(deliverJob)}
          onClose={() => setDeliverJob(null)}
          sourceType="dcp"
          sourceJobId={deliverJob.id}
          projectName={deliverJob.name}
          filename={deliverJob.package_path ? deliverJob.package_path.split('/').pop() : `${deliverJob.name}.tar`}
          nasPath={deliverJob.package_path || ''}
          jobData={deliverJob}
        />
      )}

      {/* Delete confirmation */}
      <Dialog open={Boolean(deleteConfirm)} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete DCP Job?</DialogTitle>
        <DialogContent>
          <Typography>
            Delete <strong>{deleteConfirm?.name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This will permanently delete the DCP project files, encoded output, tar package, and all associated KDM records.
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1, fontWeight: 600 }}>
            This operation cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => {
            dispatch(deleteDCPJob(deleteConfirm.id));
            setDeleteConfirm(null);
          }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DCPJobs;
